import React from 'react';

import { TextButton } from '@components/Button/variants';
import LazyImgixImage from '@components/LazyImgixImage';
import type { LeadCaptureModalBodyProps } from '@components/MarketingModal/LeadCaptureModalBody';
import LeadCaptureModalBody from '@components/MarketingModal/LeadCaptureModalBody';
import type { ModalHeaderProps } from '@components/MarketingModal/common/modalHeader';
import { useAppContext } from '@contexts/AppContext';
import useNewsletterDetailByIds from '@hooks/useNewsletterDetailByIds';
import { Theme } from '@interfaces/global';

export type LeadCaptureModalWithNewsletterProps = ModalHeaderProps & LeadCaptureModalBodyProps;

/**
 * Lead capture modal for a single newsletter.
 * Growth calls this the Acquisition P1 Modal - Single Newsletter Sign Up.
 */
const LeadCaptureModalWithNewsletter = ({
  buttonText,
  customUrl,
  leadType,
  offerLink,
  newsletterId,
  subtitle,
  newsletterImageUrl,
}: LeadCaptureModalWithNewsletterProps) => {
  const { isGdprRequired } = useAppContext();
  const { newsletters, isLoadingNewsletters } = useNewsletterDetailByIds({
    newsletter_ids: [newsletterId],
  });

  if (isLoadingNewsletters || !newsletters) {
    return null;
  }
  const newsletter = newsletters[0];

  return (
    <div className="flex w-full flex-wrap justify-center px-32 py-32 md:py-64 lg:px-0">
      <div className="flex max-w-full flex-wrap items-start gap-x-48 md:max-w-[900px] md:flex-nowrap">
        <LazyImgixImage
          src={newsletterImageUrl || newsletter.imageUrl}
          alt={newsletter.name}
          wrapperClassName="w-[260px] h-auto sm:w-[316px] md:w-[400px] md:h-[200px] flex-shrink-0 overflow-hidden"
          className="h-full w-full scale-[9/10] object-cover"
        />
        <div className="flex flex-col">
          <div className="pt-16 font-suisse-intl-mono text-overline-md text-primary-magenta md:pt-0">
            {newsletter.frequency}
            {newsletter.isSubscriberOnly && <span> (Subscriber only)</span>}
          </div>
          <div className="font-suisse-works text-headline-xxs sm:text-headline-xs lg:text-headline-sm">
            {newsletter.name}
          </div>
          <div className="font-suisse-screen text-byline-sm text-grayscale-gray">
            By {newsletter.author}
          </div>
          <div className="py-16 font-suisse-intl text-button-md text-grayscale-charcoal">
            {subtitle || newsletter.description}
          </div>
          <TextButton
            as="link"
            className="!w-fit pb-24 font-suisse-intl text-button-sm text-grayscale-charcoal sm:pb-48 md:pb-24 lg:mt-0"
            href={newsletter.readLatestPath}
          >
            See Example
          </TextButton>
          <div className="max-w-full border-0 border-t border-solid pt-24 sm:pt-48 md:max-w-[370px] md:border-t-0 md:pt-0">
            <LeadCaptureModalBody
              buttonText={buttonText}
              customUrl={customUrl}
              leadType={leadType}
              offerLink={offerLink}
              includeSignInOption={isGdprRequired}
              gdpr={isGdprRequired}
              theme={Theme.Light}
              withOauth={false}
              newsletterIds={[newsletterId]}
              emailLeadCaptureClassName="flex flex-col-reverse gap-y-16"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureModalWithNewsletter;
